import { useEffect, useState } from 'react'

import { Button } from '@krp/ui-kit'
import { RmkDialog, formatKopecks, type ShiftMark } from '@krp/sale-ui'

import { parseCashAmount } from '../model/cashAmount'
import './own-actions.css'

/** Итоги смены для панели «Смена» словами: подписи и значения готовит окно, пакет их не считает. */
export interface OwnTotalsView {
  status: 'idle' | 'loading' | 'ready' | 'failed'
  facts: readonly { label: string; value: string }[]
  /** Почему итоги не прочитаны — слова агента. */
  error: string | null
}

/** Итог последней команды кассе для показа в диалоге: «готово», отказ с советом агента или «исход неизвестен». */
export interface OwnCommandResult {
  tone: 'success' | 'warning' | 'danger'
  text: string
  advice: string | null
}

export interface OwnActionsProps {
  /** «Этот компьютер · KASSA-1 · касса и терминал». */
  where: string
  shiftMark: ShiftMark
  /** «Смена открыта, №3» / «Смена закрыта» / «Состояние смены не подтверждено». */
  shiftText: string
  /** Замечания кассы к смене (слова агента). */
  shiftProblems: readonly string[]
  /** Команда кассе идёт (чек, смена, наличные, печать): новые не запускаются. */
  busy: boolean
  /** Продажа закрыта из-за неразобранной операции: смена и наличные тоже закрыты. */
  locked: boolean
  /**
   * Нет подтверждённого кассира (RP-OWN11): смена, наличные и X-отчёт закрыты, причина — текстом рядом с кнопками. Окно кассира
   * (выбор и PIN) остаётся на экране продажи; `undefined` и `null` — кассир есть.
   */
  cashierBlockedReason?: string | null
  /** Виды цен базы 1С, из которых кассир выбирает; пусто — список не получен. */
  priceTypes: readonly { key: string; name: string }[]
  /** Действующий вид цены (выбор кассира или по умолчанию); `null` — не выбран. */
  priceTypeKey: string | null
  /** Почему вид цены сейчас менять нельзя (идёт чек); `null` — можно. */
  priceTypeLockedReason: string | null
  totals: OwnTotalsView
  result: OwnCommandResult | null
  onReadTotals: () => void
  onOpenShift: () => void
  onCloseShift: () => void
  onCash: (type: 'in' | 'out', amountKopecks: number) => void
  onPrintReportX: () => void
  onPrintReceiptCopy: () => void
  onChoosePriceType: (key: string) => void
  onClose: () => void
}

type View = 'tiles' | 'shift' | 'cash' | 'price' | 'reports'

const SHIFT_HINT: Readonly<Record<ShiftMark, string>> = {
  open: 'Закрыть смену, Z-отчёт',
  closed: 'Открыть смену',
  unknown: 'Проверить смену',
}

/**
 * «··· Действия» источника «Собственная»: смена, наличные, вид цены и отчёты — всё, что кассиру нужно РЕДКО (связь — точка «Связь» в шапке).
 * Каждая команда с деньгами просит подтверждения с суммой; пока идёт команда или не выяснен исход прошлой операции, кнопки выключены
 * и причина написана рядом.
 */
export function OwnActionsDialog(props: OwnActionsProps) {
  const [view, setView] = useState<View>('tiles')
  const { busy, locked } = props
  const cashierBlocked = props.cashierBlockedReason ?? null
  const blocked = busy
    ? 'Идёт операция на кассе — дождитесь её окончания.'
    : locked
      ? 'Сначала выясните исход прошлой операции: пока он неизвестен, смена и наличные закрыты.'
      : cashierBlocked

  const back = (
    <Button variant="ghost" onClick={() => setView('tiles')}>
      ← Действия
    </Button>
  )

  switch (view) {
    case 'shift':
      return (
        <RmkDialog title="Смена" size="lg" onClose={props.onClose}>
          <div className="own-panel">
            {back}
            <ShiftPanel {...props} blocked={blocked} />
          </div>
        </RmkDialog>
      )
    case 'cash':
      return (
        <RmkDialog title="Наличные" size="md" onClose={props.onClose}>
          <div className="own-panel">
            {back}
            <CashPanel {...props} blocked={blocked} />
          </div>
        </RmkDialog>
      )
    case 'price':
      return (
        <RmkDialog title="Вид цены" size="md" onClose={props.onClose}>
          <div className="own-panel">
            {back}
            <PricePanel {...props} />
          </div>
        </RmkDialog>
      )
    case 'reports':
      return (
        <RmkDialog title="Отчёты" size="md" onClose={props.onClose}>
          <div className="own-panel">
            {back}
            <p>X-отчёт не закрывает смену. Копия — последнего напечатанного чека.</p>
            <div className="own-panel__row">
              <Button disabled={props.busy || cashierBlocked !== null} onClick={props.onPrintReportX}>
                X-отчёт
              </Button>
              <Button disabled={props.busy} onClick={props.onPrintReceiptCopy}>
                Копия последнего чека
              </Button>
            </div>
            {props.busy && <p>Идёт операция на кассе — дождитесь её окончания.</p>}
            {cashierBlocked !== null && <p role="status">{cashierBlocked}</p>}
            <Result result={props.result} />
          </div>
        </RmkDialog>
      )
    case 'tiles':
      return (
        <RmkDialog title="Действия" size="lg" onClose={props.onClose}>
          <div className="own-panel">
            <div className="own-actions__tiles">
              <Tile title="Смена" hint={SHIFT_HINT[props.shiftMark]} onClick={() => setView('shift')} />
              <Tile title="Наличные" hint="Внести или выплатить" onClick={() => setView('cash')} />
              <Tile title="Вид цены" hint={priceHint(props)} onClick={() => setView('price')} />
              <Tile title="Отчёты" hint="X-отчёт, копия чека" onClick={() => setView('reports')} />
            </div>
            <Result result={props.result} />
            <dl className="own-panel__facts" aria-label="Где вы работаете">
              <dt>Где</dt>
              <dd>{props.where}</dd>
              <dt>Смена</dt>
              <dd>{props.shiftText}</dd>
            </dl>
          </div>
        </RmkDialog>
      )
  }
}

function priceHint(props: Pick<OwnActionsProps, 'priceTypes' | 'priceTypeKey'>): string {
  if (props.priceTypeKey === null) return 'Не выбран'
  return props.priceTypes.find((type) => type.key === props.priceTypeKey)?.name ?? 'Выбран'
}

function Tile({ title, hint, onClick }: { title: string; hint: string; onClick: () => void }) {
  return (
    <button type="button" className="own-tile" onClick={onClick}>
      <span className="own-tile__title">{title}</span>
      <span className="own-tile__hint">{hint}</span>
    </button>
  )
}

function Result({ result }: { result: OwnCommandResult | null }) {
  if (result === null) return null
  return (
    <div className="own-panel__result" data-tone={result.tone} role={result.tone === 'danger' ? 'alert' : 'status'}>
      <p>{result.text}</p>
      {result.advice !== null && <p>{result.advice}</p>}
    </div>
  )
}

function ShiftPanel(props: OwnActionsProps & { blocked: string | null }) {
  const [confirmClose, setConfirmClose] = useState(false)
  const { onReadTotals } = props
  // Итоги читаются при входе в панель: кассир видит, что закрывает, до нажатия.
  useEffect(() => {
    onReadTotals()
  }, [onReadTotals])
  const totals = props.totals
  return (
    <>
      <p data-mark={props.shiftMark}>{props.shiftText}</p>
      {props.shiftProblems.length > 0 && (
        <ul aria-label="Замечания кассы">
          {props.shiftProblems.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
      {totals.status === 'loading' && <p role="status">Читаем итоги смены…</p>}
      {totals.status === 'failed' && <p role="alert">{totals.error ?? 'Итоги смены не прочитаны.'}</p>}
      {totals.status === 'ready' && totals.facts.length > 0 && (
        <dl className="own-panel__facts" aria-label="Итоги смены">
          {totals.facts.map((fact) => (
            <div key={fact.label} style={{ display: 'contents' }}>
              <dt>{fact.label}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {confirmClose ? (
        <div className="own-panel__result" data-tone="warning" role="group" aria-label="Закрыть смену?">
          <p>Закрыть смену? Касса напечатает Z-отчёт; продавать можно будет после открытия новой смены.</p>
          <div className="own-panel__row">
            <Button
              variant="danger"
              disabled={props.blocked !== null}
              onClick={() => {
                setConfirmClose(false)
                props.onCloseShift()
              }}
            >
              Закрыть смену
            </Button>
            <Button variant="ghost" onClick={() => setConfirmClose(false)}>
              Отмена
            </Button>
          </div>
        </div>
      ) : (
        <div className="own-panel__row">
          {props.shiftMark !== 'open' && (
            <Button variant="primary" disabled={props.blocked !== null} onClick={props.onOpenShift}>
              Открыть смену
            </Button>
          )}
          {props.shiftMark === 'open' && (
            <Button disabled={props.blocked !== null} onClick={() => setConfirmClose(true)}>
              Закрыть смену
            </Button>
          )}
          <Button variant="ghost" onClick={props.onReadTotals}>
            Обновить итоги
          </Button>
        </div>
      )}
      {props.blocked !== null && <p>{props.blocked}</p>}
      <Result result={props.result} />
    </>
  )
}

function CashPanel(props: OwnActionsProps & { blocked: string | null }) {
  const [text, setText] = useState('')
  const [pending, setPending] = useState<'in' | 'out' | null>(null)
  const amount = parseCashAmount(text)
  const error = text.trim() === '' || amount.ok ? null : amount.text
  const verb = pending === 'in' ? 'Внести' : 'Выплатить'
  return (
    <>
      <label className="own-panel__row">
        <span>Сумма, ₽</span>
        <input
          className="own-panel__input"
          inputMode="decimal"
          value={text}
          aria-invalid={error !== null}
          aria-describedby={error === null ? undefined : 'own-cash-error'}
          disabled={pending !== null}
          onChange={(event) => setText(event.target.value)}
        />
      </label>
      {error !== null && (
        <p id="own-cash-error" role="alert">
          {error}
        </p>
      )}
      {pending !== null && amount.ok ? (
        <div className="own-panel__result" data-tone="warning" role="group" aria-label="Подтвердите операцию">
          <p>
            {verb} {formatKopecks(amount.kopecks)} {pending === 'in' ? 'в кассу' : 'из кассы'}? Операция попадёт в чековую ленту и отчёты смены.
          </p>
          <div className="own-panel__row">
            <Button
              variant="primary"
              disabled={props.blocked !== null}
              onClick={() => {
                setPending(null)
                setText('')
                props.onCash(pending, amount.kopecks)
              }}
            >
              {verb}
            </Button>
            <Button variant="ghost" onClick={() => setPending(null)}>
              Отмена
            </Button>
          </div>
        </div>
      ) : (
        <div className="own-panel__row">
          <Button disabled={!amount.ok || props.blocked !== null} onClick={() => setPending('in')}>
            Внести
          </Button>
          <Button disabled={!amount.ok || props.blocked !== null} onClick={() => setPending('out')}>
            Выплатить
          </Button>
        </div>
      )}
      {props.blocked !== null && <p>{props.blocked}</p>}
      <Result result={props.result} />
    </>
  )
}

function PricePanel(props: OwnActionsProps) {
  const locked = props.priceTypeLockedReason
  return (
    <>
      <p>Цены строк берутся из прайса 1С по выбранному виду цены. Менять вид цены можно между чеками.</p>
      {props.priceTypes.length === 0 ? (
        <p role="status">Виды цен не получены: проверьте настройку источника и связь с платформой.</p>
      ) : (
        <ul className="own-panel__list" aria-label="Виды цен">
          {props.priceTypes.map((type) => (
            <li key={type.key}>
              <Button
                variant={props.priceTypeKey === type.key ? 'primary' : 'secondary'}
                aria-pressed={props.priceTypeKey === type.key}
                disabled={locked !== null}
                onClick={() => props.onChoosePriceType(type.key)}
              >
                {type.name}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {locked !== null && <p>{locked}</p>}
    </>
  )
}